import { PlusIcon, TrashIcon, PencilIcon, SyncIcon } from '@primer/octicons-react';
import { Box, IconButton, useConfirm } from '@primer/react';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import Button from '../../components/ui/button/Button';
import DataTable from '../../components/ui/data-table';
import { IColumn } from '../../components/ui/data-table/DataTable';
import Text from '../../components/ui/text';

import { useCommonContext } from '../../contexts/common';
import { NotifyHelper } from '../../helpers/toast';

import MeetingBusyDetailModal from './MeetingBusyDetailModal';
import { meetingApi } from '../../api/meetingApi';
import { IMeetingBusy } from '../../models/response/meeting/IMeetingBusy';
import { meetingBusyApi } from '../../api/meetingBusyApi';
import { useCommonSelectedHook } from '../../hooks/useCommonSelectedHook';

const MeetingBusyPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [dataSource, setDataSource] = useState<IMeetingBusy[]>([]);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [selectedId, setSelectedId] = useState<number>(0);
    const { nam_hoc } = useCommonSelectedHook();

    const { translate } = useCommonContext();
    const confirm = useConfirm();

    useEffect(() => {
        handleReloadAsync();
    }, []);

    const handleReloadAsync = async () => {
        setIsLoading(true);

        const res = await meetingBusyApi.selectByGv(nam_hoc);

        setIsLoading(false);

        if (res.is_success) {
            setDataSource(res.data ?? []);
        } else {
            NotifyHelper.Error(res.message ?? 'Có lỗi');
        }
    };

    const onDelete = async (id: number) => {
        if (
            await confirm({
                content: 'Bạn có chắc chắn muốn xóa lịch bận này?',
                title: 'Xác nhận',
                cancelButtonContent: 'Không',
                confirmButtonContent: 'Xóa',
                confirmButtonType: 'danger'
            })
        ) {
            const res = await meetingBusyApi.delete(id);

            if (res.is_success) {
                NotifyHelper.Success('Success');
                handleReloadAsync();
            } else {
                NotifyHelper.Error(res.message ?? 'Error');
            }
        }
    };

    const columns = useMemo((): IColumn[] => {
        return [
            {
                dataField: 'ghi_chu',
                caption: 'Nội dung',
                minWidth: '200px',
                isMainColumn: true
            },
            {
                dataField: 'start_time',
                caption: 'Từ thời gian',
                minWidth: '100px',
                cellRender: (data: IMeetingBusy) => (
                    <Box>
                        {moment(data.start_time).format('DD/MM/YYYY HH:mm')}
                    </Box>
                )
            },
            {
                dataField: 'end_time',
                caption: 'Đến thời gian',
                minWidth: '100px',
                cellRender: (data: IMeetingBusy) => (
                    <>
                        {moment(data.end_time).format('DD/MM/YYYY HH:mm')}
                    </>
                )
            },
            {
                id: 'cmd',
                width: '120px',
                caption: translate('Base.Label.Command'),
                cellRender: (data: IMeetingBusy) => {
                    return (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                icon={PencilIcon}
                                aria-label=''
                                variant='default'
                                onClick={() => {
                                    setSelectedId(data.id);
                                    setIsOpenModal(true);
                                }}
                            />

                            <IconButton
                                icon={TrashIcon}
                                aria-label=''
                                variant='danger'
                                onClick={() => {
                                    onDelete(data.id);
                                }}
                            />
                        </Box>
                    );
                }
            }
        ];
    }, []);

    return (
        <Box
            sx={{
                p: 3
            }}
        >
            <DataTable
                titleComponent={
                    <Text
                        text='Lịch bận giáo viên'
                        sx={{
                            fontSize: '24px'
                        }}
                    />
                }
                subTitle={`${translate('Base.Label.TotalCount')}: ${(dataSource?.length ?? 0).toLocaleString()}`}
                searchEnable
                isLoading={isLoading}
                actionComponent={
                    <>
                        <Button
                            text='Thêm mới'
                            leadingVisual={PlusIcon}
                            variant='primary'
                            onClick={() => {
                                setSelectedId(0);
                                setIsOpenModal(true);
                            }}
                        />
                        <Button
                            sx={{ ml: '2' }}
                            text='Làm mới'
                            leadingVisual={SyncIcon}
                            onClick={handleReloadAsync}
                        />
                    </>
                }
                columns={columns}
                data={dataSource}
                height={`${window.innerHeight - 250}px`}
            />

            {isOpenModal && (
                <MeetingBusyDetailModal
                    isOpen={isOpenModal}
                    id={selectedId}
                    onClose={() => setIsOpenModal(false)}
                    onSuccess={() => {
                        handleReloadAsync();
                        setIsOpenModal(false);
                    }}
                />
            )}
        </Box>
    );
};

export default MeetingBusyPage;